<template>
  <v-autocomplete
    :model-value="selectedValue"
    :items="visibleSets"
    :loading="loading"
    item-title="name"
    item-value="_key"
    return-object
    :multiple="multiple"
    clearable
    hide-details
    hide-no-data
    :label="label"
    class="app-select"
    autocomplete="off"
    @update:model-value="$emit('update:modelValue', $event || null)"
  >
    <template v-if="multiple" #selection="{ item }">
      <CommonChip
        closable
        @click:close="removeSelectedSet(item.raw)"
      >
        {{ item.raw.name }}
      </CommonChip>
    </template>
    <template #item="{ item, props: itemProps }">
      <v-list-item v-bind="itemProps">
        <template #append>
          <CommonChip v-if="showCount" size="x-small">{{ getSetCountLabel(item.raw) }}</CommonChip>
          <SavedSetTypeChip v-else size="x-small" :set-class="item.raw.class" />
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue';
import { mapState } from 'vuex';

import { Nullable } from '@/utils/types';
import { ServiceFactory } from '@/services/ServiceFactory';
import { IDefaultFilters } from '@/bundles/Panels/interfaces';
import { useNotifications } from '@/bundles/App/composables/useNotifications';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import SavedSetTypeChip from '@/bundles/SavedSets/components/chips/SavedSetTypeChip.vue';

const FilterViewService = ServiceFactory.get('filter-view');

interface ISavedSetSelectData {
  sets: IDefaultFilters[];
  loading: boolean;
}

export default defineComponent({
  name: 'SavedSetSelect',

  components: { CommonChip, SavedSetTypeChip },

  props: {
    source: {
      type: String,
      required: true,
    },
    // A plain set id is a valid value: `selectedValue` resolves it back to its record.
    modelValue: {
      type: [Object, Array, String] as PropType<Nullable<IDefaultFilters> | IDefaultFilters[] | string>,
      default: null,
    },
    label: {
      type: String,
      default: 'Saved Set',
    },
    onlyStatic: Boolean,
    showCount: Boolean,
    multiple: Boolean,
    preselectId: {
      type: String as PropType<Nullable<string>>,
      default: null,
    },
  },

  // The root is the v-autocomplete, which emits `update:modelValue` itself. Without this
  // declaration a parent's v-model handler stays in `$attrs` and falls through onto it,
  // so every selection would also run the parent setter with the raw autocomplete value.
  emits: ['update:modelValue'],

  setup () {
    const { addNotification } = useNotifications();

    return { addNotification };
  },

  data: (): ISavedSetSelectData => ({
    sets: [],
    loading: false,
  }),

  computed: {
    ...mapState({
      token: (state: any) => state.idToken,
    }),
    visibleSets (): IDefaultFilters[] {
      return this.onlyStatic ? this.sets.filter((set) => set.class === 'static') : this.sets;
    },
    // `return-object` means the autocomplete only derives a label from a set record, so a plain
    // id held by the parent (deep link) has to be mapped back to its set here.
    selectedValue (): Nullable<IDefaultFilters> | IDefaultFilters[] {
      if (typeof this.modelValue !== 'string') {
        return this.modelValue;
      }

      return this.sets.find((set) => set._key === this.modelValue) ?? null;
    },
  },

  watch: {
    preselectId (next) {
      this.$emit('update:modelValue', next);
      // An id arriving later (back/forward) may sit outside the fetched page — refetch to pull it in.
      if (next && !this.sets.some((set) => set._key === next)) {
        this.fetchSets();
      }
    },
  },

  created () {
    this.fetchSets();
  },

  mounted () {
    /* The id alone is enough for the parent to hold as the value: with_ids guarantees that set is
       in the response, and `selectedValue` resolves the label out of `sets`. */
    if (this.preselectId) {
      this.$emit('update:modelValue', this.preselectId);
    }
  },

  methods: {
    async fetchSets () {
      try {
        this.loading = true;
        const preselectId = this.preselectId;
        const params = [{ name: 'list', value: this.source }];
        if (preselectId) {
          params.push({ name: 'with_ids', value: preselectId });
        }
        const { data } = await FilterViewService.list({ token: this.token, params });
        this.sets = data.result;
        // with_ids returns the set whenever the account can open it, so a missing one (deleted, another account's key) is dropped.
        if (preselectId && preselectId === this.preselectId && !this.sets.some((set) => set._key === preselectId)) {
          this.$emit('update:modelValue', null);
        }
      } catch (error: any) {
        this.addNotification(error);
      } finally {
        this.loading = false;
      }
    },
    getSetCountLabel (item: IDefaultFilters): string {
      const count = item.savedSet?.count?.length ?? 0;

      return `${count} records`;
    },
    removeSelectedSet (item: IDefaultFilters) {
      this.$emit('update:modelValue', (this.modelValue as IDefaultFilters[]).filter((set) => set._key !== item._key));
    },
  },
});
</script>
